import { getCalendarDaysUntilDate } from '@noths/polaris-client-utils';

import type { DeliveryState } from 'src/redux/delivery/slice';
import type { DeliverySettingsAPIDeliveryOption } from 'src/services/delivery-settings/types/DeliverySettingsAPIDeliveryOption';

const getMinMaxDays = (deliveryOption: DeliverySettingsAPIDeliveryOption) => {
  if (deliveryOption.date) {
    const daysUntilDate = getCalendarDaysUntilDate(new Date(deliveryOption.date));
    return { minDays: daysUntilDate, maxDays: daysUntilDate };
  }

  return { minDays: deliveryOption.min!, maxDays: deliveryOption.max! };
};

const splitToHandlingAndTransitDays = (days: number) => {
  // assume half is handling, half is transit; round up transit, round down handling
  const transitDays = Math.ceil(days / 2);
  const handlingDays = days - transitDays;

  return { handlingDays, transitDays };
};

export const getShippingDetailsJSONLDSchema = (delivery: DeliveryState) => {
  const mainlandUKDeliveryZone = delivery.options.find(
    (option) => option.zoneCode === 'MAINLAND_UK',
  );

  if (!mainlandUKDeliveryZone) {
    return [];
  }

  return mainlandUKDeliveryZone.deliveryOptions.map((option) => {
    const { maxDays, minDays } = getMinMaxDays(option);
    const { handlingDays: minHandlingDays, transitDays: minTransitDays } =
      splitToHandlingAndTransitDays(minDays);
    const { handlingDays: maxHandlingDays, transitDays: maxTransitDays } =
      splitToHandlingAndTransitDays(maxDays);
    const gbpPrice = option.prices.find((price) => price.currencyCode === 'GBP');

    return {
      '@type': 'OfferShippingDetails' as const,
      deliveryTime: {
        '@type': 'ShippingDeliveryTime' as const,
        handlingTime: {
          '@type': 'QuantitativeValue' as const,
          minValue: minHandlingDays,
          maxValue: maxHandlingDays,
          unitCode: 'd' as const,
        },
        transitTime: {
          '@type': 'QuantitativeValue' as const,
          minValue: minTransitDays,
          maxValue: maxTransitDays,
          unitCode: 'd' as const,
        },
      },
      shippingDestination: {
        '@type': 'DefinedRegion' as const,
        addressCountry: 'GB' as const,
      },
      ...(gbpPrice
        ? {
            shippingRate: {
              '@type': 'MonetaryAmount' as const,
              currency: 'GBP' as const,
              value: gbpPrice.amount,
            },
          }
        : {}),
    };
  });
};
